import React, { SyntheticEvent, useContext, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { ComponentCenteredSpinner } from '@amzn/aapn-react-components'
import {
  AlertContainer,
  BillingInfoLabel,
  BillingInfoTextElement,
  BlockTooltip,
  Box,
  BoxElement,
  CardHeaderSubheader,
  ChangeFileLink,
  DescriptionList,
  DropArea,
  EmptySubmissionHistory,
  EmptySubmissionHistoryText,
  FileUploadDescriptionList,
  ReportDownloadLink,
  StatusCell,
  StatusDescription,
  StatusIcon,
  StyledAlert,
  StyledCard,
  StyledErrorIcon,
  StyledSpinner,
  StyledSuccessIcon,
  StyledTable,
  StyledWarningIcon,
  TableCell,
  TableHeader,
  TableRow,
  TermConditionText,
} from './create_sponsored_ads_accounts.styles'
import { CardContainer, ContentContainer, MainContainer } from '../focus_view_shared.styles'
import { Button, CardHeader, Divider, FileUploadDropZone, Icon, InlineMessage, ListItem, Text } from '@amzn/storm-ui'
import { getMetricsPublisherAsync, publishCounter } from '@amzn/aapn-front-end-metrics'
import { StageDiveConfig, StageDiveConfigContext } from '../../../../../util/app_initialization/appconfig'
import {
  BulkAccountCreationRequestFailedReason,
  BulkAccountCreationRequestStatus,
  useGetBulkAccountCreationRequestUploadUrlLazyQuery,
  useGetBulkAccountsCreationHistoryQuery,
  useGetBulkAccountsCreationReportDownloadUrlLazyQuery,
  useGetBulkAccountsCreationTemplateDownloadUrlLazyQuery,
  useGetPartnerInfoQuery,
  useSubmitBulkAccountCreationMutation,
} from '../../../../../generated/graphql'
import { PARTNER_ID_HEADER_KEY } from '../../../../../util/app_initialization/apollo_client/stagedive_apollo_client_provider'
import { GraphQLError } from 'graphql'
import { toNullable } from 'fp-ts/es6/Option'
import { useLoginContext } from 'stagedive/LoginContext'
import * as i18nKeyList from './create_access_request_18n_mappings'
import { getManageAccountsURL } from '../ads_console_utils'
import * as AACChromeAPI from '@amzn/aac-chrome-api'
import { download as downloadIcon } from '@amzn/storm-ui-icons'
import { ExternalLink, RotatingFontAwesomeIcon } from '@amzn/aapn-react-components'
import { UploadHistory } from './create_sponsored_ads_model'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useLanguageContext } from 'stagedive/LanguageContext'
import { getDateTimeFormat } from '../../../../../util/date_helper'
import * as sponsoredAdsStrings from './create_sponsored_ads_account_i18n_mappings'
import { Redirect } from 'react-router-dom'
import { convertNullableToUndefined } from '../../../../../util/graphql_type_helper'
import Skeleton from 'react-loading-skeleton'
import { download } from '../../../../../util/download'
import { AlertType } from '@amzn/storm-ui/dist/types/Alert/types'
import * as TE from 'fp-ts/es6/TaskEither'
import { tryCatch } from 'fp-ts/lib/TaskEither'
import { pipe } from 'fp-ts/es6/function'

type AlertState =
  | 'error_created'
  | 'error_created_generic'
  | 'error_created_user_input_generic'
  | 'error_send'
  | 'info_send'
  | 'success_created'
  | 'warning_account_created_with_issues'
  | 'error_created_user_input_accounts_limit'
  | 'warning_created'
  | undefined

export const MAX_FILE_SIZE = 5242880
export const CONTENT_TYPE_XSLX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
export const NE_HISTORY_POLLING_INTERVAL = 1000
export const MAX_ACCOUNTS_LIMIT = 100
const XLSX_HEX_SIGNATURE = [0x50, 0x4b, 0x03, 0x04]

export const CreateSponsoredAdsAccounts: React.FunctionComponent = (): JSX.Element => {
  const stageDiveConfig = useContext<StageDiveConfig>(StageDiveConfigContext)
  const { t } = useTranslation()
  const [sendRequest, { loading: sendLoading }] = useSubmitBulkAccountCreationMutation()
  const [alertState, setAlertState] = useState<AlertState>(undefined)
  const { loginContext } = useLoginContext()
  const partnerAccountId = toNullable(loginContext.partnerProfileId) ?? ''
  const loggedInUserName = toNullable(loginContext.userName) ?? ''
  const loggedInUserEmail = toNullable(loginContext.userEmail) ?? ''
  const {
    data: uploadHistoryRequest,
    error: uploadHistoryError,
    loading: uploadHistoryLoading,
    refetch: uploadHistoryRefetch,
  } = useGetBulkAccountsCreationHistoryQuery({
    context: {
      headers: {
        [PARTNER_ID_HEADER_KEY]: partnerAccountId,
      },
    },
  })
  const {
    data: getPartnerInfoResult,
    error: getPartnerInfoError,
    loading: getPartnerInfoLoading,
  } = useGetPartnerInfoQuery({
    context: {
      headers: {
        [PARTNER_ID_HEADER_KEY]: partnerAccountId,
      },
    },
    skip: partnerAccountId === null,
    notifyOnNetworkStatusChange: true,
  })
  const [getUploadURL, { data: uploadUrl, loading: uploadUrlLoading, error: uploadUrlError }] = useGetBulkAccountCreationRequestUploadUrlLazyQuery({
    context: {
      headers: {
        [PARTNER_ID_HEADER_KEY]: partnerAccountId,
      },
    },
  })
  const [getDownloadURL, { data: downloadUrl, loading: downloadUrlLoading, error: downloadUrlError }] = useGetBulkAccountsCreationReportDownloadUrlLazyQuery({
    context: {
      headers: {
        [PARTNER_ID_HEADER_KEY]: partnerAccountId,
      },
    },
  })

  const [getTemplateDownloadURL, { data: templateDownloadUrl, loading: templateDownloadUrlLoading, error: templateDownloadUrlError }] =
    useGetBulkAccountsCreationTemplateDownloadUrlLazyQuery({
      context: {
        headers: {
          [PARTNER_ID_HEADER_KEY]: partnerAccountId,
        },
      },
    })

  const { language } = useLanguageContext()
  const [fileUploadedRequestId, setFileUploadedRequestId] = useState<string>('')
  const [fileUploading, setFileUploading] = useState<File | undefined>(undefined)
  const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState('')
  const [existPendingRequest, setExistPendingRequest] = useState(false)
  const [refetchingHistory, setRefetchingHistory] = useState(false)
  const existPendingRequestRef = useRef(false)
  const refetchingHistoryRef = useRef(false)
  const [uploadHistories, setUploadHistories] = useState<UploadHistory[]>([])
  const partnerAddress = getPartnerInfoResult?.partner?.liveRegistration?.registration?.companyAddress
  const partnerAddressString = [
    partnerAddress?.lines?.join(','),
    partnerAddress?.districtOrCounty,
    partnerAddress?.city,
    partnerAddress?.stateOrRegion,
    partnerAddress?.postalCode,
    partnerAddress?.countryCode,
  ]
    .filter((element) => element !== undefined)
    .join(',')

  useEffect(() => {
    const newExistPendingRequest = uploadHistories.some((history: UploadHistory) => history.status === BulkAccountCreationRequestStatus.Pending)

    if (!newExistPendingRequest && existPendingRequest) {
      switch (uploadHistories[0].status) {
        case BulkAccountCreationRequestStatus.Complete:
          setAlertState('success_created')
          break
        case BulkAccountCreationRequestStatus.FailedPartially:
          setAlertState('warning_created')
          break
        case BulkAccountCreationRequestStatus.Failed:
          if (uploadHistories[0].failedReason && uploadHistories[0].failedReason === BulkAccountCreationRequestFailedReason.ServerFailed) {
            setAlertState('error_created_generic')
          } else if (uploadHistories[0].failedReason && uploadHistories[0].failedReason === BulkAccountCreationRequestFailedReason.UserError) {
            setAlertState('error_created_user_input_generic')
          } else if (uploadHistories[0].failedReason && uploadHistories[0].failedReason === BulkAccountCreationRequestFailedReason.UserErrorAccountLimit) {
            setAlertState('error_created_user_input_accounts_limit')
          } else {
            setAlertState('error_created')
          }
          break
        case BulkAccountCreationRequestStatus.AccountsCreatedWithIncompleteSetup:
          setAlertState('warning_account_created_with_issues')
          break
      }
    }

    setExistPendingRequest(newExistPendingRequest)
  }, [uploadHistories, existPendingRequest])

  useEffect(() => {
    existPendingRequestRef.current = existPendingRequest
    refetchingHistoryRef.current = refetchingHistory

    if (!refetchingHistory && existPendingRequest && !uploadHistoryLoading) {
      setRefetchingHistory(true)

      setTimeout(function refetchHistory() {
        if (refetchingHistoryRef.current && existPendingRequestRef.current) {
          uploadHistoryRefetch()
          setTimeout(refetchHistory, NE_HISTORY_POLLING_INTERVAL)
        } else {
          setRefetchingHistory(false)
        }
      }, NE_HISTORY_POLLING_INTERVAL)
    }
  }, [existPendingRequest, existPendingRequestRef, setRefetchingHistory, uploadHistoryRefetch, refetchingHistory, refetchingHistoryRef])

  /* istanbul ignore next */
  useEffect(() => {
    const config = AACChromeAPI.getChromeConfiguration()
    const pageHeader = new AACChromeAPI.PageHeader()
    const objectInfo = new AACChromeAPI.ObjectInfo()
    const actionList = new AACChromeAPI.ItemList()

    config.setPageLayoutType('FOCUS')
    const focusModeExitEventId = 'ExitFocusView'

    const button: AACChromeAPI.BUTTON = new AACChromeAPI.Button()
    const sendButtonId = 'sendButtonId'
    button.setId(sendButtonId)

    button.setLabel(t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_FOCUS_VIEW_SUBMIT))
    button.setButtonType(AACChromeAPI.ButtonType.PRIMARY)
    button.setState(
      sendLoading
        ? AACChromeAPI.ActionState.LOADING
        : uploadHistoryLoading || existPendingRequest || fileUploadedRequestId === ''
        ? AACChromeAPI.ActionState.DISABLED
        : AACChromeAPI.ActionState.ENABLED,
    )

    //TODO: Add tooltip to button when disabled once bug on Chrome side gets fixed: https://t.corp.amazon.com/P126343067/overview

    AACChromeAPI.getActionEventTarget().addEventListener(sendButtonId, onClickSendRequest)

    const backToAccountsLink = new AACChromeAPI.Button()
    const backToAccountsLinkButtonId = 'backToAccountsLink'
    backToAccountsLink.setId(backToAccountsLinkButtonId)

    backToAccountsLink.setLabel(t(i18nKeyList.CREATE_ACCESS_REQUEST_BACK_TO_ACCOUNTS))
    backToAccountsLink.setButtonType(AACChromeAPI.ButtonType.LINK)

    const backToAccountsLinkFn = (): void => {
      window.location.href = getManageAccountsURL(partnerAccountId, stageDiveConfig.hostname)
    }
    AACChromeAPI.getActionEventTarget().addEventListener(backToAccountsLinkButtonId, backToAccountsLinkFn)
    AACChromeAPI.getCommonEventTarget().addEventListener(focusModeExitEventId, backToAccountsLinkFn)

    actionList.addItem(backToAccountsLink)
    actionList.addItem(button)

    objectInfo.setActions(actionList)

    pageHeader.setObjectInfo(objectInfo)
    pageHeader.setPageName(t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_FOCUS_VIEW_TITLE))
    config.setPageHeader(pageHeader)
    AACChromeAPI.setChromeConfiguration(config)
    return () => {
      AACChromeAPI.getActionEventTarget().removeEventListener(sendButtonId, onClickSendRequest)
      AACChromeAPI.getActionEventTarget().removeEventListener(backToAccountsLinkButtonId, backToAccountsLinkFn)
      AACChromeAPI.getCommonEventTarget().addEventListener(focusModeExitEventId, backToAccountsLinkFn)
    }
  }, [sendLoading, fileUploadedRequestId, uploadHistories, t])

  const alertStateValueMap = (alertState: AlertState): { header: string; description: string } => {
    switch (alertState) {
      case 'info_send':
        return {
          header: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_INFO_HEADER,
          description: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_INFO_DESCRIPTION,
        }
      case 'error_send':
        return {
          header: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ERROR_HEADER,
          description: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ERROR_DESCRIPTION,
        }
      case 'success_created':
        return {
          header: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ACCOUNTS_CREATED_HEADER,
          description: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ACCOUNTS_CREATED_DESCRIPTION,
        }
      case 'warning_created':
        return {
          header: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ACCOUNTS_CREATED_HEADER,
          description: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ACCOUNTS_PARTIALLY_CREATED_DESCRIPTION,
        }
      case 'error_created':
        return {
          header: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ACCOUNTS_NOT_CREATED_HEADER,
          description: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ACCOUNTS_NOT_CREATED_DESCRIPTION,
        }
      case 'error_created_generic':
        return {
          header: '',
          description: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ACCOUNTS_NOT_CREATED_GENERIC_DESCRIPTION,
        }
      case 'error_created_user_input_generic':
        return {
          header: '',
          description: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ACCOUNTS_NOT_CREATED_USER_INPUT_GENERIC_DESCRIPTION,
        }
      case 'error_created_user_input_accounts_limit':
        return {
          header: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ACCOUNTS_NOT_CREATED_USER_INPUT_ACCOUNT_LIMIT_HEADER,
          description: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ACCOUNTS_NOT_CREATED_USER_INPUT_ACCOUNT_LIMIT_DESCRIPTION,
        }
      case 'warning_account_created_with_issues':
        return {
          header: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ACCOUNTS_CREATED_WITH_ISSUES_HEADER,
          description: sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ACCOUNTS_CREATED_WITH_ISSUES_DESCRIPTION,
        }
      default:
        return { header: '', description: '' }
    }
  }

  const onClickSendRequest = (): void => {
    setAlertState(undefined)
    sendRequest({
      variables: {
        input: {
          requestId: fileUploadedRequestId,
          fileName: fileUploading?.name ?? '',
        },
      },
      context: {
        headers: {
          [PARTNER_ID_HEADER_KEY]: partnerAccountId,
        },
      },
    })
      .then(() => {
        uploadHistoryRefetch()
        setAlertState('info_send')
        setFileUploadedRequestId('')
        setFileUploading(undefined)
        getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
          publishCounter('send.success', 1)(p)
        })
      })
      .catch((_: GraphQLError) => {
        uploadHistoryRefetch()
        setAlertState('error_send')
        getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
          publishCounter('send.fail', 1)(p)
        })
      })
  }

  const generateDescriptions = (): JSX.Element => {
    const descriptions = [t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALLOWED_EXTENSIONS), t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALLOWED_FILE_SIZE)]

    return (
      <FileUploadDescriptionList>
        {descriptions.map((description: string, i: number) => (
          <ListItem key={i}>
            <Text fontSize="small">{description}</Text>
          </ListItem>
        ))}
      </FileUploadDescriptionList>
    )
  }

  useEffect(() => {
    if (uploadHistoryRequest?.bulkAccountCreationUploadHistory?.__typename === 'BulkAccountCreationUploadHistoryOutput') {
      setUploadHistories(
        uploadHistoryRequest?.bulkAccountCreationUploadHistory?.uploadRequests.map((uploadRequest) => ({
          filename: uploadRequest.fileName,
          timestamp: uploadRequest.submittedAt,
          userEmail: uploadRequest.customerEmail,
          status: uploadRequest.status,
          reportId: convertNullableToUndefined(uploadRequest.reportId),
          accountsTotal: convertNullableToUndefined(uploadRequest.totalRequestedAccounts),
          accountsCreated: convertNullableToUndefined(uploadRequest.numberOfCreatedAccounts),
          failedReason: convertNullableToUndefined(uploadRequest?.failedReason),
        })),
      )
    }
  }, [uploadHistoryRequest, setUploadHistories])

  useEffect(() => {
    if (!uploadUrlLoading && uploadUrl && fileUploading) {
      if (uploadUrl.bulkAccountCreationRequestUploadURL.__typename === 'RequestFileLocation') {
        getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
          publishCounter('getUploadURL.success', 1)(p)
        })
        const { url, requestId } = uploadUrl.bulkAccountCreationRequestUploadURL

        fetch(url, {
          method: 'PUT',
          body: fileUploading,
          headers: {
            'Content-Type': CONTENT_TYPE_XSLX,
          },
        })
          .then((r) => {
            if (r.ok) {
              getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
                publishCounter('uploadToS3.success', 1)(p)
              })
              setFileUploadedRequestId(requestId)
            } else {
              getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
                publishCounter('uploadToS3.fail', 1)(p)
              })
              setFileUploadErrorMessage(t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_FILE_UPLOAD_ERROR))
              setFileUploading(undefined)
            }
          })
          .catch((_) => {
            setFileUploadErrorMessage(t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_FILE_UPLOAD_ERROR))
            setFileUploading(undefined)
          })
      } else {
        getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
          publishCounter('getUploadURL.fail', 1)(p)
        })
        setFileUploadErrorMessage(t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_FILE_UPLOAD_ERROR))
        setFileUploading(undefined)
      }
    }
  }, [uploadUrl, uploadUrlLoading, fileUploading])

  useEffect(() => {
    if (!downloadUrlLoading && downloadUrl) {
      if (downloadUrl.bulkAccountCreationReportDownloadURL.__typename === 'ReportFileLocation') {
        getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
          publishCounter('getDownloadURL.success', 1)(p)
        })
        const { url } = downloadUrl.bulkAccountCreationReportDownloadURL
        download(url, 'report', 'xlsx')
      } else {
        getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
          publishCounter('getDownloadURL.fail', 1)(p)
        })
      }
    } else {
      getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
        publishCounter('getDownloadURL.fail', 1)(p)
      })
    }
  }, [downloadUrl, downloadUrlLoading])

  const reportDownloadLinkComponent = (uploadHistory: UploadHistory): JSX.Element => {
    return (
      <ReportDownloadLink
        downloadURlLoading={downloadUrlLoading}
        onClick={() => {
          uploadHistory.reportId &&
            getDownloadURL({
              variables: {
                input: {
                  reportId: uploadHistory.reportId,
                },
              },
              context: {
                headers: {
                  [PARTNER_ID_HEADER_KEY]: partnerAccountId,
                },
              },
            })
        }}
      >
        {t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_TABLE_ACTION_DOWNLOAD_REPORT)}
      </ReportDownloadLink>
    )
  }

  useEffect(() => {
    if (!templateDownloadUrlLoading && templateDownloadUrl) {
      if (templateDownloadUrl.bulkAccountCreationTemplateDownloadURL.__typename === 'TemplateFileLocation') {
        getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
          publishCounter('getTemplateDownloadURL.success', 1)(p)
        })
        const { url } = templateDownloadUrl.bulkAccountCreationTemplateDownloadURL

        download(url, 'BulkAccountTemplate', 'xlsx')
      } else {
        getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
          publishCounter('getTemplateDownloadURL.fail', 1)(p)
        })
      }
    } else {
      getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
        publishCounter('getTemplateDownloadURL.fail', 1)(p)
      })
    }
  }, [templateDownloadUrl, templateDownloadUrlLoading, templateDownloadUrlError])
  const generateStatusCell = (uploadHistory?: UploadHistory): JSX.Element => (
    <TableRow key={uploadHistory ? `${uploadHistory.filename}${uploadHistory.timestamp}` : `loading`}>
      <TableCell>
        <Text>{uploadHistory ? uploadHistory.filename : <Skeleton />}</Text>
        <Text>{uploadHistory ? getDateTimeFormat(new Date(uploadHistory.timestamp), language) : <Skeleton />}</Text>
      </TableCell>
      <TableCell>
        <Text>{uploadHistory ? uploadHistory.userEmail : <Skeleton />}</Text>
      </TableCell>
      <TableCell>
        <Text>
          {!uploadHistory ? (
            <Skeleton />
          ) : uploadHistory.status === BulkAccountCreationRequestStatus.Pending ? (
            <StatusCell data-testid={'account-creation-request-pending'}>
              <StatusIcon>
                <StyledSpinner>
                  <RotatingFontAwesomeIcon icon={{ prefix: 'fas', iconName: 'spinner' }} />
                </StyledSpinner>
              </StatusIcon>
              <StatusDescription>
                <Text>
                  <Trans
                    i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_TABLE_STATUS_ACCOUNT_CREATED}
                    values={{ accountsCreated: uploadHistory.accountsCreated, accountsTotal: uploadHistory.accountsTotal }}
                  />
                </Text>
              </StatusDescription>
            </StatusCell>
          ) : uploadHistory.status === BulkAccountCreationRequestStatus.Complete ? (
            <StatusCell data-testid={'account-creation-request-complete'}>
              <StatusIcon>
                <StyledSuccessIcon>
                  <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'check' }} />
                </StyledSuccessIcon>
              </StatusIcon>
              <StatusDescription>
                <Text>
                  <Trans
                    i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_TABLE_STATUS_ACCOUNT_CREATED}
                    values={{ accountsCreated: uploadHistory.accountsCreated, accountsTotal: uploadHistory.accountsTotal }}
                  />
                </Text>
              </StatusDescription>
            </StatusCell>
          ) : uploadHistory.status === BulkAccountCreationRequestStatus.Failed &&
            uploadHistory.failedReason &&
            uploadHistory.failedReason === BulkAccountCreationRequestFailedReason.ServerFailed ? (
            <StatusCell data-testid={'account-creation-request-failed-generic'}>
              <StatusIcon>
                <StyledErrorIcon>
                  <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'exclamation' }} />
                </StyledErrorIcon>
              </StatusIcon>
              <StatusDescription>
                <Text>
                  <Trans i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ACCOUNTS_NOT_CREATED_GENERIC_DESCRIPTION} />
                </Text>
              </StatusDescription>
            </StatusCell>
          ) : uploadHistory.status === BulkAccountCreationRequestStatus.Failed &&
            uploadHistory.failedReason &&
            uploadHistory.failedReason === BulkAccountCreationRequestFailedReason.UserError ? (
            <StatusCell data-testid={'account-creation-request-failed-user-input-generic'}>
              <StatusIcon>
                <StyledErrorIcon>
                  <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'exclamation' }} />
                </StyledErrorIcon>
              </StatusIcon>
              <StatusDescription>
                <Text>
                  <Trans i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ACCOUNTS_NOT_CREATED_USER_INPUT_GENERIC_DESCRIPTION} />
                </Text>
              </StatusDescription>
            </StatusCell>
          ) : uploadHistory.status === BulkAccountCreationRequestStatus.Failed &&
            uploadHistory.failedReason &&
            uploadHistory.failedReason === BulkAccountCreationRequestFailedReason.UserErrorAccountLimit ? (
            <StatusCell data-testid={'account-creation-request-failed-user-input-accounts-limit'}>
              <StatusIcon>
                <StyledErrorIcon>
                  <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'exclamation' }} />
                </StyledErrorIcon>
              </StatusIcon>
              <StatusDescription>
                <Text>
                  <Trans
                    i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_ALERT_ACCOUNTS_NOT_CREATED_USER_INPUT_ACCOUNT_LIMIT_HEADER}
                    values={{ accountsLimit: MAX_ACCOUNTS_LIMIT }}
                  />
                </Text>
              </StatusDescription>
            </StatusCell>
          ) : uploadHistory.status === BulkAccountCreationRequestStatus.Failed ? (
            <StatusCell data-testid={'account-creation-request-failed'}>
              <StatusIcon>
                <StyledErrorIcon>
                  <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'exclamation' }} />
                </StyledErrorIcon>
              </StatusIcon>
              <StatusDescription>
                <Text>
                  <Trans
                    i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_TABLE_STATUS_ACCOUNT_CREATED}
                    values={{ accountsCreated: uploadHistory.accountsCreated, accountsTotal: uploadHistory.accountsTotal }}
                  />
                </Text>
                {uploadHistory.reportId ? reportDownloadLinkComponent(uploadHistory) : undefined}
              </StatusDescription>
            </StatusCell>
          ) : uploadHistory.status === BulkAccountCreationRequestStatus.FailedPartially ? (
            <StatusCell data-testid={'account-creation-request-partially-failed'}>
              <StatusIcon>
                <StyledWarningIcon>
                  <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'exclamation-triangle' }} />
                </StyledWarningIcon>
              </StatusIcon>
              <StatusDescription>
                <Text>
                  <Trans
                    i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_TABLE_STATUS_ACCOUNT_CREATED}
                    values={{ accountsCreated: uploadHistory.accountsCreated, accountsTotal: uploadHistory.accountsTotal }}
                  />
                </Text>
                {uploadHistory.reportId ? reportDownloadLinkComponent(uploadHistory) : undefined}
              </StatusDescription>
            </StatusCell>
          ) : (
            uploadHistory.status === BulkAccountCreationRequestStatus.AccountsCreatedWithIncompleteSetup && (
              <StatusCell data-testid={'account-created-with-issues'}>
                <StatusIcon>
                  <StyledWarningIcon>
                    <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'exclamation-triangle' }} />
                  </StyledWarningIcon>
                </StatusIcon>
                <StatusDescription>
                  <Text>
                    <Trans
                      i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_TABLE_STATUS_ACCOUNT_CREATED_WITH_ISSUES}
                      values={{ accountsCreated: uploadHistory.accountsCreated, accountsTotal: uploadHistory.accountsTotal }}
                    />
                  </Text>
                  {uploadHistory.reportId ? reportDownloadLinkComponent(uploadHistory) : undefined}
                </StatusDescription>
              </StatusCell>
            )
          )}
        </Text>
      </TableCell>
    </TableRow>
  )

  const getAlertType = (alertState: AlertState): AlertType => {
    switch (alertState?.split('_').shift()) {
      case 'success':
        return 'success'
      case 'warning':
        return 'warning'
      case 'error':
        return 'error'
      default:
        return 'info'
    }
  }

  const readFileAsArrayBuffer = (file: File): TE.TaskEither<Error, Uint8Array> => {
    return tryCatch(
      () =>
        new Promise<Uint8Array>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => {
            if (reader.result !== null) {
              resolve(new Uint8Array(reader.result as ArrayBuffer))
            } else {
              reject(new Error('Failed to read file'))
            }
          }
          reader.onerror = () => {
            reject(reader.error)
          }
          reader.readAsArrayBuffer(file)
        }),
      (reason) => new Error(String(reason)),
    )
  }

  // This checks the magic bytes for xlsx files and fails if not matching. Here is a wiki: https://en.wikipedia.org/wiki/List_of_file_signatures
  const checkIfFileIsXLSX = (file: Uint8Array): boolean => file.slice(0, 4).every((byte, index) => byte === XLSX_HEX_SIGNATURE[index])

  if (getPartnerInfoLoading) {
    return <ComponentCenteredSpinner />
  }

  if (uploadHistoryError || uploadUrlError || downloadUrlError || getPartnerInfoError) {
    return <Redirect push to="/error" />
  }

  if (getPartnerInfoResult?.partner?.isPartnerTrusted === false) {
    return <Redirect push to="/error/unauthorized_access" />
  }

  return (
    <MainContainer>
      <ContentContainer>
        <AlertContainer>
          {uploadHistories.length && alertState ? (
            <StyledAlert
              type={getAlertType(alertState)}
              isOpen={!!alertState}
              withCloseButton={true}
              onClose={() => setAlertState(undefined)}
              header={t(alertStateValueMap(alertState).header, {
                accountCreatedNum: uploadHistories[0].accountsCreated,
                totalAccountNum: uploadHistories[0].accountsTotal,
                accountsLimit: MAX_ACCOUNTS_LIMIT,
              })}
            >
              <Text>{t(alertStateValueMap(alertState).description)}</Text>
            </StyledAlert>
          ) : undefined}
        </AlertContainer>
        <CardContainer>
          <StyledCard>
            <Text>
              <Trans i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_DESCRIPTION} />
            </Text>
            <Text>
              <DescriptionList>
                <ListItem>
                  <Text>{t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_INSTRUCTION_1)}</Text>
                </ListItem>
                <ListItem>
                  <Text>
                    <Trans i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_INSTRUCTION_2} />
                  </Text>
                </ListItem>
                <ListItem>
                  <Text>{t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_INSTRUCTION_3)}</Text>
                </ListItem>
              </DescriptionList>
            </Text>
            <Text withMargin>
              <Trans i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_BILLING_DESCRIPTION_1} />
            </Text>
            <Box>
              <BoxElement>
                <Trans
                  i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_BILLING_NAME}
                  values={{ nameValue: loggedInUserName }}
                  components={[<BillingInfoLabel key={'business-label1'} />, <BillingInfoTextElement key={'business-element1'} />]}
                />
              </BoxElement>
              <BoxElement>
                <Trans
                  i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_BILLING_EMAIL}
                  values={{ emailValue: loggedInUserEmail }}
                  components={[<BillingInfoLabel key={'business-label2'} />, <BillingInfoTextElement key={'business-element2'} />]}
                />
              </BoxElement>
              <BoxElement>
                <Trans
                  i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_BILLING_ADDRESS}
                  values={{ addressValue: partnerAddressString }}
                  components={[<BillingInfoLabel key={'business-label3'} />, <BillingInfoTextElement key={'business-element3'} />]}
                />
              </BoxElement>
            </Box>
            <Text withMargin>
              <Trans i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_BILLING_DESCRIPTION_2} />
            </Text>
            <Divider />
            <Button
              onClick={() => {
                getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
                  publishCounter('downloadTemplate', 1)(p)
                })
                getTemplateDownloadURL()
              }}
            >
              <Icon type={downloadIcon} size="sm" /> {t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_DOWNLOAD_TEMPLATE)}
            </Button>
            <DropArea>
              <BlockTooltip
                trigger={
                  <div>
                    <FileUploadDropZone
                      dropzoneId="drop-zone"
                      accept={['xlsx']}
                      inputId="file-uploader"
                      multiple={false}
                      disabled={fileUploading !== undefined || fileUploadedRequestId !== '' || uploadUrlLoading || existPendingRequest || uploadHistoryLoading}
                      heading={
                        <Text type="h5" textColor="base">
                          {t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_FILE_UPLOAD_ACTION_PROMPT)}
                        </Text>
                      }
                      description={generateDescriptions()}
                      /* Pushing a custom onClick function to the FileUploadDropZone in order to clear the previous upload on a click.
                         This makes sure that if the user removes an uploaded file and uploads the same file again, the upload is registered. (This is not supported by storm as of date).
                         SIM for the issue we faced using UAT: https://sim.amazon.com/issues/AAPN-9572
                         SIM opened on storm to fix this: https://sim.amazon.com/issues/CFX-3265
                      */
                      onClick={() => {
                        const element = document.getElementById('file-uploader') as HTMLInputElement
                        element.value = ''
                        element.files = null
                      }}
                      onChange={(_: SyntheticEvent, files: File[]) => {
                        if (files && files.length === 1) {
                          getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
                            publishCounter('fileUpload', 1)(p)
                          })
                          if (files[0].size >= MAX_FILE_SIZE) {
                            setFileUploadErrorMessage(t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_FILE_SIZE_ERROR))
                            getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
                              publishCounter('fileUpload.error', 1)(p)
                              publishCounter('fileUpload.error.size', 1)(p)
                            })
                          } else if (files[0].name.split('.').pop() !== 'xlsx') {
                            setFileUploadErrorMessage(t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_FILE_TYPE_ERROR))
                            getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
                              publishCounter('fileUpload.error', 1)(p)
                              publishCounter('fileUpload.error.type', 1)(p)
                            })
                          } else {
                            pipe(
                              readFileAsArrayBuffer(files[0]),
                              TE.match(
                                () => {
                                  setFileUploadErrorMessage(t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_FILE_UPLOAD_ERROR))
                                  getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
                                    publishCounter('fileUpload.error', 1)(p)
                                  })
                                },
                                (arrayBuffer) => {
                                  if (!checkIfFileIsXLSX(arrayBuffer)) {
                                    setFileUploadErrorMessage(t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_FILE_TYPE_ERROR))
                                    getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
                                      publishCounter('fileUpload.error', 1)(p)
                                      publishCounter('fileUpload.error.type', 1)(p)
                                    })
                                  } else {
                                    setFileUploadErrorMessage('')
                                    getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
                                      publishCounter('fileUpload.success', 1)(p)
                                    })
                                    getUploadURL()
                                    setFileUploading(files[0])
                                  }
                                },
                              ),
                            )()
                          }
                        }
                      }}
                      fullWidth={true}
                    />
                  </div>
                }
                message={t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_WAIT_TIP)}
                inert={!existPendingRequest || uploadHistoryLoading}
                nonInteractive={true}
                type="dark"
              />
            </DropArea>
            {fileUploadedRequestId !== '' ? (
              <StatusCell>
                <StatusIcon>
                  <StyledSuccessIcon>
                    <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'check' }} />
                  </StyledSuccessIcon>
                </StatusIcon>
                <StatusDescription>
                  <Text>
                    {`${fileUploading?.name} `}
                    <ChangeFileLink
                      onClick={() => {
                        getMetricsPublisherAsync('CreateSponsoredAdsAccounts').then((p) => {
                          publishCounter('changeFile', 1)(p)
                        })
                        setFileUploadedRequestId('')
                        setFileUploading(undefined)
                        setFileUploadErrorMessage('')
                      }}
                    >
                      {t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_REMOVE_FILE)}
                    </ChangeFileLink>
                  </Text>
                </StatusDescription>
              </StatusCell>
            ) : fileUploadErrorMessage ? (
              <InlineMessage messageType="error" message={fileUploadErrorMessage} />
            ) : fileUploading !== undefined && fileUploadedRequestId === '' ? (
              <InlineMessage messageType="loading" message={t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_FILE_UPLOADING)} />
            ) : undefined}
            <TermConditionText>
              <Trans
                i18nKey={sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_TERM_CONDITION_TEXT}
                components={[<ExternalLink href={'https://advertising.amazon.com/terms'} key={0} />]}
              />
            </TermConditionText>
          </StyledCard>
        </CardContainer>
        <CardContainer>
          <StyledCard>
            <CardHeader>
              <Text type="h2">{t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_HEADER_SUBMISSION_HISTORY)}</Text>
              <CardHeaderSubheader>{t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_HEADER_SUBMISSION_HISTORY_SUBHEADER)}</CardHeaderSubheader>
            </CardHeader>
            <StyledTable>
              <thead>
                <tr>
                  <TableHeader>{t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_TABLE_HEADER_FILE)}</TableHeader>
                  <TableHeader>{t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_TABLE_HEADER_USER)}</TableHeader>
                  <TableHeader>{t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_TABLE_HEADER_STATUS)}</TableHeader>
                </tr>
              </thead>
              <tbody>
                {uploadHistoryLoading ? (
                  generateStatusCell()
                ) : uploadHistories.length === 0 ? (
                  <EmptySubmissionHistory colSpan={3}>
                    <EmptySubmissionHistoryText>{t(sponsoredAdsStrings.CREATE_BULK_ACCOUNTS_TABLE_NO_SUBMISSIONS)}</EmptySubmissionHistoryText>
                  </EmptySubmissionHistory>
                ) : (
                  uploadHistories.map((uploadHistory) => generateStatusCell(uploadHistory))
                )}
              </tbody>
            </StyledTable>
          </StyledCard>
        </CardContainer>
      </ContentContainer>
    </MainContainer>
  )
}
